import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import AnimatedArrow from "./AnimatedArrow";
import PartnersMarquee from "./PartnersMarquee";
import Reveal from "./Reveal";
import useAutoplayVideo from "../hooks/useAutoplayVideo";
import { useLanguage } from "../context/LanguageContext";

// Matches the media queries on the <link rel="preload"> hints in
// index.html — those hints start the same file downloading before React
// even mounts, so resolving to the identical URL here is what lets the
// video element's fetch pick up the already-in-flight (or already
// cached) request instead of starting a second one from scratch.
function resolveHeroVideoSrc() {
  return window.matchMedia("(max-width: 639px)").matches
    ? "/assets/video/hero-culinary-mobile.mp4"
    : "/assets/video/hero-culinary.mp4";
}

export default function Hero() {
  const { t } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Resolved once, at initial render, and passed as a real `src` attribute
  // (not <source media> — multiple <source> children previously raced
  // WebKit's resource-selection algorithm against useAutoplayVideo's
  // imperative .play() call, leaving iOS's tap-to-play glyph showing over
  // the poster instead of actually autoplaying) — the video element then
  // starts fetching as soon as it's in the DOM, not one effect-tick after
  // mount the way setting video.src imperatively did.
  const [videoSrc] = useState(resolveHeroVideoSrc);

  useAutoplayVideo(videoRef);

  return (
    <section
      id="top"
      className="relative flex h-screen h-dvh min-h-[560px] w-full flex-col justify-end overflow-hidden bg-ink sm:min-h-[640px] md:min-h-[720px]"
    >
      {/* videoSrc (resolved above) picks the ~3.7x lighter 960x540 phone
          encode below Tailwind's sm breakpoint (640px) so a cellular
          connection has less to buffer before autoplay can start. */}
      <video
        ref={videoRef}
        src={videoSrc}
        className="absolute inset-0 h-full w-full object-cover object-[50%_75%]"
        poster="/assets/video/hero-poster.jpg"
        preload="auto"
        autoPlay
        muted
        loop
        playsInline
        disablePictureInPicture
        disableRemotePlayback
      />
      <div className="absolute inset-0 bg-black/25" />

      <div className="container-industrial relative z-10 flex flex-col gap-8 pb-8 pt-8 sm:gap-10 sm:pb-10 sm:pt-32 md:gap-14 md:pb-12 md:pt-40">
        <div className="max-w-4xl">
          {/* will-change promotes each element to its own compositor layer
              up front, so the fade-up animates purely on the GPU instead of
              competing with the hero video's decode/paint work for the main
              thread — without it, the two starting at the same instant on
              mount visibly stuttered on real mobile hardware. */}
          <h1
            className="animate-heroFadeUp will-change-[opacity,transform] font-display uppercase text-[clamp(4rem,3rem+5vw,7.5rem)] font-normal tracking-[0.3px] leading-[1.05] text-white"
            style={{ animationDelay: "100ms" }}
          >
            {t("heroTitleLine1")}
            <span className="block text-white/90">{t("heroTitleLine2")}</span>
          </h1>
          <p
            className="animate-heroFadeUp will-change-[opacity,transform] mt-5 max-w-xl text-base leading-relaxed text-white/70 sm:mt-6 md:text-lg"
            style={{ animationDelay: "280ms" }}
          >
            {t("heroSubtitle")}
          </p>
          <div
            className="animate-heroFadeUp will-change-[opacity,transform] mt-6 flex flex-wrap gap-4 sm:mt-9"
            style={{ animationDelay: "460ms" }}
          >
            {/* Full-width and a fixed height on mobile so both buttons
                match each other exactly while stacked (they'd otherwise
                each size to their own content — "Оставить заявку" + arrow
                vs. the longer, border-only "Изучить оборудование" render
                at slightly different widths AND heights); both revert to
                natural content-sized sizing once they sit side by side at
                sm+, matching every other btn-primary/btn-outline pair on
                the site. */}
            <Link to="/catalog" className="btn-primary h-[52px] w-full sm:h-auto sm:w-auto">
              {t("heroCtaPrimary")}
              <AnimatedArrow />
            </Link>
            <Link to="/contacts" className="btn-outline h-[52px] w-full sm:h-auto sm:w-auto">
              {t("heroCtaSecondary")}
            </Link>
          </div>
        </div>
      </div>

      {/* Hidden on mobile — the logo band moves to its own light section
          right after Hero there instead (see App.tsx), since a transparent
          band over the video needs the extra vertical space more than a
          small screen can spare. */}
      <Reveal className="relative z-10 hidden border-t border-white/10 bg-black/10 py-5 backdrop-blur-md sm:block sm:py-6 md:py-8">
        <PartnersMarquee />
      </Reveal>
    </section>
  );
}
